import { ChangeEvent, FC, useId, useState } from 'react';

import { DirectUpload } from '@rails/activestorage';
import { ImagePlus, Loader2, X } from 'lucide-react';

import { Button } from '../../../../components/ui/Button';
import { OfferPhoto, OfferPhotoUpload } from '../../../../types';

const DIRECT_UPLOAD_URL = '/profile/direct_uploads';
const MAX_PHOTOS_COUNT = 5;
const MAX_PHOTO_SIZE = 8 * 1024 * 1024;
const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

interface Props {
    photos: OfferPhotoUpload[];
    onChange: (photos: OfferPhotoUpload[]) => void;
    required?: boolean;
    error?: string;
    existingPhotos?: OfferPhoto[];
    removedPhotoIds?: number[];
    onRemoveExistingPhoto?: (photoId: number) => void;
    onUploadingChange?: (uploading: boolean) => void;
    disabled?: boolean;
}

const uploadPhoto = (file: File): Promise<OfferPhotoUpload> =>
    new Promise((resolve, reject) => {
        const upload = new DirectUpload(file, DIRECT_UPLOAD_URL);

        upload.create((error, blob) => {
            if (error || !blob) {
                reject(new Error(error ?? 'Upload failed'));
                return;
            }

            resolve({
                signed_id: blob.signed_id,
                filename: blob.filename,
                preview_url: URL.createObjectURL(file),
            });
        });
    });

export const OfferPhotoUploader: FC<Props> = ({
    photos,
    onChange,
    required = false,
    error,
    existingPhotos = [],
    removedPhotoIds = [],
    onRemoveExistingPhoto,
    onUploadingChange,
    disabled = false,
}) => {
    const inputId = useId();
    const [uploading, setUploading] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);
    const visibleExistingPhotos = existingPhotos.filter(
        (photo) => !removedPhotoIds.includes(photo.id),
    );
    const photosCount = visibleExistingPhotos.length + photos.length;

    const handleFiles = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? []);
        event.target.value = '';
        setLocalError(null);

        if (files.length === 0) return;

        if (photosCount + files.length > MAX_PHOTOS_COUNT) {
            setLocalError(`Можна додати максимум ${MAX_PHOTOS_COUNT} фото`);
            return;
        }

        const invalidFormat = files.some(
            (file) => !ALLOWED_PHOTO_TYPES.includes(file.type),
        );
        if (invalidFormat) {
            setLocalError('Дозволені лише JPG, PNG та WebP');
            return;
        }

        const oversizedFile = files.some((file) => file.size > MAX_PHOTO_SIZE);
        if (oversizedFile) {
            setLocalError('Розмір одного фото не може перевищувати 8 MB');
            return;
        }

        setUploading(true);
        onUploadingChange?.(true);

        try {
            const uploadedPhotos = await Promise.all(files.map(uploadPhoto));
            onChange([...photos, ...uploadedPhotos]);
        } catch {
            setLocalError('Не вдалося завантажити фото. Спробуйте ще раз');
        } finally {
            setUploading(false);
            onUploadingChange?.(false);
        }
    };

    const removeUploadedPhoto = (photo: OfferPhotoUpload) => {
        if (photo.preview_url.startsWith('blob:')) {
            URL.revokeObjectURL(photo.preview_url);
        }

        onChange(
            photos.filter(
                (candidate) => candidate.signed_id !== photo.signed_id,
            ),
        );
    };

    return (
        <div className="space-y-2" data-testid="offer-photo-uploader">
            <div className="flex flex-col items-start gap-1.5">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={
                        disabled || uploading || photosCount >= MAX_PHOTOS_COUNT
                    }
                    asChild
                >
                    <label htmlFor={inputId} className="cursor-pointer">
                        {uploading ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <ImagePlus className="mr-2 h-4 w-4" />
                        )}
                        {uploading ? 'Завантаження…' : 'Додати фото'}
                    </label>
                </Button>
                <p className="text-xs text-muted-foreground">
                    До 5 фото, JPG, PNG або WebP, максимум 8 MB кожне
                </p>
                <input
                    id={inputId}
                    className="sr-only"
                    type="file"
                    accept={ALLOWED_PHOTO_TYPES.join(',')}
                    multiple
                    aria-required={required}
                    disabled={
                        disabled || uploading || photosCount >= MAX_PHOTOS_COUNT
                    }
                    onChange={handleFiles}
                    data-testid="offer-photo-input"
                />
            </div>

            {photosCount > 0 && (
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                    {visibleExistingPhotos.map((photo) => (
                        <div
                            key={`existing-${photo.id}`}
                            className="group relative aspect-square overflow-hidden rounded-xl border bg-muted"
                        >
                            <img
                                src={photo.thumbnail_url}
                                alt={photo.filename}
                                className="h-full w-full object-cover"
                            />
                            <button
                                type="button"
                                className="absolute top-1 right-1 rounded-full bg-background/90 p-1 shadow"
                                aria-label={`Видалити ${photo.filename}`}
                                onClick={() =>
                                    onRemoveExistingPhoto?.(photo.id)
                                }
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    ))}
                    {photos.map((photo) => (
                        <div
                            key={photo.signed_id}
                            className="group relative aspect-square overflow-hidden rounded-xl border bg-muted"
                        >
                            {photo.preview_url ? (
                                <img
                                    src={photo.preview_url}
                                    alt={photo.filename}
                                    className="h-full w-full object-cover"
                                />
                            ) : (
                                <div className="flex h-full items-center justify-center p-2 text-center text-xs text-muted-foreground">
                                    {photo.filename}
                                </div>
                            )}
                            <button
                                type="button"
                                className="absolute top-1 right-1 rounded-full bg-background/90 p-1 shadow"
                                aria-label={`Видалити ${photo.filename}`}
                                onClick={() => removeUploadedPhoto(photo)}
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {(localError || error) && (
                <p className="text-sm text-red-600">{localError ?? error}</p>
            )}
        </div>
    );
};
